import {
  convertCouponInputToRequest,
  convertPauseInputToRequest,
  CouponInputFormik,
  PauseInputFormik,
} from "@modules/boxSubscription/formik/boxSubscriptionFormik"
import { useServicesContext } from "@modules/core/services/services.context"
import { ProgramBundleSubscriberEntity } from "@modules/programBundle/programBundleSubscribers/entities/programBundleSubscriberEntity"
import { ProgramBundleSubscriberDto } from "@modules/programBundle/programBundleSubscribers/gateway/programBundleSubscriberDto"
import { ProgramSubscriberEntity } from "@modules/programs/programSubscriber/entities/programSubscriberEntity"
import { ProgramSubscriberDto } from "@modules/programs/programSubscriber/gateway/programSubscriberDto"
import { useInfiniteQuery, useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"
import { useMemo } from "react"

export enum UseProgramSubscriberDetailsKey {
  PROGRAM_SUBSCRIBER = "programSubscriber",
  PROGRAM_BUNDLE_SUBSCRIBER = "programBundleSubscriber",
}

export const useProgramSubscriberDetails = (
  key: UseProgramSubscriberDetailsKey,
  programId: string,
  subscriberId: string,
) => {
  const { programSubscriberApi, programBundleSubscriberApi } = useServicesContext()
  const subscriberQueryKey = [key, programId, subscriberId]

  const { data, isLoading, refetch, error } = useSuspenseQuery({
    queryKey: subscriberQueryKey,
    queryFn: async () => {
      if (key === UseProgramSubscriberDetailsKey.PROGRAM_BUNDLE_SUBSCRIBER) {
        const data = await programBundleSubscriberApi.getProgramBundleSubscriber(programId, subscriberId)
        return ProgramBundleSubscriberEntity.fromItemDto(data)
      } else {
        const data = await programSubscriberApi.getProgramSubscriber(programId, subscriberId)
        return ProgramSubscriberEntity.fromItemDto(data)
      }
    },
  })

  return {
    data,
    isLoading,
    refetch,
    error,
  }
}

export const useProgramSubscribers = (programId: string) => {
  const { programSubscriberApi } = useServicesContext()

  const { data, isLoading, refetch, fetchNextPage } = useInfiniteQuery({
    queryKey: ["programSubscribers", programId],
    queryFn: async ({ pageParam }) => {
      return programSubscriberApi.getProgramSubscribers(programId, pageParam, 50)
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const subscriberList = useMemo(
    () =>
      data?.pages.flatMap((page) => page.content).map((content) => ProgramSubscriberEntity.fromItemDto(content)) ?? [],
    [data],
  )

  return {
    data: {
      ...data,
      content: subscriberList,
      totalSize: data?.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    refetch,
    fetchNextPage,
  }
}

export const useProgramSubscriberSearch = (programId: string, searchTerm?: string) => {
  const { programSubscriberApi } = useServicesContext()

  const { data, isLoading, refetch, isFetching, fetchNextPage } = useInfiniteQuery({
    queryKey: ["programSubscribersSearch", programId, searchTerm?.toLocaleLowerCase()],
    queryFn: async ({ pageParam }) => {
      return programSubscriberApi.searchProgramSubscribers(programId, pageParam, 40, searchTerm?.toLocaleLowerCase())
    },
    staleTime: 1000 * 60 * 5,
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      if (lastPage.content.length === 0) {
        return null
      }
      return lastPage.page + 1
    },
  })

  const subscriberList = useMemo(
    () =>
      data?.pages.flatMap((page) => page.content).map((content) => ProgramSubscriberEntity.fromItemDto(content)) ?? [],
    [data],
  )

  console.log("data", data)

  return {
    data: {
      ...data,
      content: subscriberList,
      totalSize: data?.pages[0]?.totalSize ?? 0,
    },
    isLoading,
    refetch,
    isFetching,
    fetchNextPage,
  }
}

export type ProgramSubscriberActions = {
  updatePause: (pause: PauseInputFormik) => Promise<void>
  resumeSubscription: () => Promise<void>
  isResumeSubscriptionLoading: boolean
  updateInternalNote: (note: string) => Promise<ProgramSubscriberEntity | ProgramBundleSubscriberEntity>
  isUpdateInternalNote: boolean
  removeSubscriber: (cancelAtPeriodEnd: boolean) => Promise<void>
  applySubscriptionDiscount: (coupon: CouponInputFormik) => Promise<void>
  isApplyingSubscriptionDiscount: boolean
  removeSubscriptionDiscount: () => Promise<void>
  isRemovingSubscriptionDiscount: boolean
}

export const useProgramSubscriberActions = (
  programId: string,
  subscriberDetails: ProgramSubscriberEntity | ProgramBundleSubscriberEntity,
): ProgramSubscriberActions => {
  const { programSubscriberApi, programBundleSubscriberApi } = useServicesContext()
  const queryClient = useQueryClient()
  const isBundle = subscriberDetails instanceof ProgramBundleSubscriberEntity
  const subscriberId =
    isBundle && subscriberDetails.programBundleSubscriptionId
      ? subscriberDetails.programBundleSubscriptionId
      : subscriberDetails.id

  const api = isBundle ? programBundleSubscriberApi : programSubscriberApi
  const subscriberQueryKey = isBundle
    ? ["programBundleSubscriber", programId, subscriberId]
    : ["programSubscriber", programId, subscriberId]
  const subscribersQueryKey = isBundle ? ["programBundleSubscribers", programId] : ["programSubscribers", programId]
  const subscribersSearchQueryKey = isBundle
    ? ["programBundleSubscribersSearch", programId]
    : ["programSubscribersSearch", programId]

  // Resume subscription
  const { mutateAsync: resumeSubscription, isPending: isResumeSubscriptionLoading } = useMutation({
    mutationFn: () => {
      return api.resumeSubscription(programId, subscriberId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  // Pause subscription
  const { mutateAsync: updatePause } = useMutation({
    mutationFn: (pause: PauseInputFormik) => {
      return api.updateSubscriberPause(programId, subscriberId, convertPauseInputToRequest(pause))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  // Update internal note
  const { mutateAsync: updateInternalNote, isPending: isUpdateInternalNote } = useMutation({
    mutationFn: async (note: string) => {
      const data = await api.updateSubscriberInternalNote(programId, subscriberId, note)
      return isBundle
        ? ProgramBundleSubscriberEntity.fromItemDto(data as ProgramBundleSubscriberDto)
        : ProgramSubscriberEntity.fromItemDto(data as ProgramSubscriberDto)
    },
    onSuccess: (data) => {
      queryClient.setQueryData(subscriberQueryKey, data)
    },
  })

  // Remove subscriber
  const { mutateAsync: removeSubscriber } = useMutation({
    mutationFn: (cancelAtPeriodEnd: boolean) => {
      return api.removeSubscriber(programId, subscriberId, cancelAtPeriodEnd)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscribersQueryKey,
      })
      queryClient.removeQueries({
        queryKey: subscriberQueryKey,
      })
      queryClient.removeQueries({
        queryKey: subscribersSearchQueryKey,
      })
    },
  })

  // Add subscription discount
  const { mutateAsync: applySubscriptionDiscount, isPending: isApplyingSubscriptionDiscount } = useMutation({
    mutationFn: (coupon: CouponInputFormik) => {
      return api.applySubscriptionDiscount(programId, subscriberId, convertCouponInputToRequest(coupon))
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  // Remove subscription discount
  const { mutateAsync: removeSubscriptionDiscount, isPending: isRemovingSubscriptionDiscount } = useMutation({
    mutationFn: () => {
      return api.removeSubscriptionDiscount(programId, subscriberId)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: subscriberQueryKey,
      })
    },
  })

  return {
    updatePause,
    resumeSubscription,
    isResumeSubscriptionLoading,
    updateInternalNote,
    isUpdateInternalNote,
    removeSubscriber,
    applySubscriptionDiscount,
    isApplyingSubscriptionDiscount,
    removeSubscriptionDiscount,
    isRemovingSubscriptionDiscount,
  }
}
